/**
 * Servicing-request vocabularies, mirrored from
 * `backend/apps/renewals/models.py::ServiceRequest`.
 *
 * B2: the list page carried its own invented sets -- statuses
 * `open / pending_customer / pending_insurer / resolved / closed` and types
 * `cancellation / claim_support / document_request / query`, none of which
 * exist on the model. Filtering by any of them could never match a row, and
 * the create modal defaulted `request_type` to `query`, which the serializer
 * rejects outright. Both screens now import from here so they cannot drift
 * from the model or from each other again.
 */

export interface Option {
  value: string;
  label: string;
}

/** ServiceRequest.STATUS_CHOICES */
export const REQUEST_STATUSES: Option[] = [
  { value: 'submitted', label: 'Submitted' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'pending_documents', label: 'Pending Documents' },
  { value: 'pending_payment', label: 'Pending Payment' },
  { value: 'with_insurer', label: 'With Insurer' },
  { value: 'completed', label: 'Completed' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
];

/** ServiceRequest.REQUEST_TYPE_CHOICES */
export const REQUEST_TYPES: Option[] = [
  { value: 'endorsement', label: 'Endorsement' },
  { value: 'duplicate_policy', label: 'Duplicate Policy' },
  { value: 'policy_copy', label: 'Policy Copy' },
  { value: 'certificate', label: 'Certificate Request' },
  { value: 'ncb_certificate', label: 'NCB Certificate' },
  { value: 'claim_status', label: 'Claim Status Inquiry' },
  { value: 'premium_receipt', label: 'Premium Receipt' },
  { value: 'policy_cancellation', label: 'Policy Cancellation' },
  { value: 'refund', label: 'Refund Request' },
  { value: 'address_change', label: 'Address Change' },
  { value: 'nominee_change', label: 'Nominee Change' },
  { value: 'contact_update', label: 'Contact Update' },
  { value: 'vehicle_transfer', label: 'Vehicle Transfer' },
  { value: 'hypothecation', label: 'Hypothecation Addition/Removal' },
  { value: 'general_inquiry', label: 'General Inquiry' },
  { value: 'complaint', label: 'Complaint' },
  { value: 'other', label: 'Other' },
];

/** ServiceRequest.PRIORITY_CHOICES */
export const REQUEST_PRIORITIES: Option[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const labelOf = (options: Option[], value: string) =>
  options.find((o) => o.value === value)?.label ?? value;

export const statusLabel = (value: string) => labelOf(REQUEST_STATUSES, value);
export const requestTypeLabel = (value: string) => labelOf(REQUEST_TYPES, value);
export const priorityLabel = (value: string) => labelOf(REQUEST_PRIORITIES, value);

/** Statuses that mean the request is finished; the resolve control hides. */
export const TERMINAL_STATUSES = ['completed', 'rejected', 'cancelled'];

/** Open and past its SLA. Trusts the server flag, and also checks the clock so a
 *  request that went overdue since the last sweep still reads as overdue. */
export function isRequestOverdue(
  request: { status: string; sla_due_date?: string | null; is_sla_breached?: boolean },
  now: number = Date.now(),
): boolean {
  if (TERMINAL_STATUSES.includes(request.status)) return false;
  if (request.is_sla_breached) return true;
  return !!request.sla_due_date && new Date(request.sla_due_date).getTime() < now;
}

export function priorityBadgeVariant(priority: string) {
  switch (priority) {
    case 'urgent':
      return 'error' as const;
    case 'high':
      return 'warning' as const;
    case 'medium':
      return 'info' as const;
    default:
      return 'default' as const;
  }
}

/**
 * `getStatusBadgeVariant` in components/common knows most of these already
 * (submitted/completed/rejected/cancelled/pending_payment/in_progress), but not
 * the servicing-only ones, which would otherwise all render grey.
 */
export function requestStatusBadgeVariant(status: string) {
  switch (status) {
    case 'completed':
      return 'success' as const;
    case 'rejected':
    case 'cancelled':
      return 'error' as const;
    case 'in_progress':
    case 'with_insurer':
      return 'info' as const;
    case 'pending_documents':
    case 'pending_payment':
      return 'warning' as const;
    case 'acknowledged':
    case 'submitted':
      return 'info' as const;
    default:
      return 'default' as const;
  }
}

/** Map a ServiceRequestActivity.activity_type onto an ActivityTimeline dot. */
export function activityTone(
  activityType: string
): 'create' | 'update' | 'success' | 'error' | 'info' {
  switch (activityType) {
    case 'created':
      return 'create';
    case 'completed':
      return 'success';
    case 'rejected':
      return 'error';
    case 'status_change':
    case 'assigned':
      return 'update';
    default:
      return 'info';
  }
}
